import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { observationsAPI, extractionReviewAPI } from '../services/api';
import {
  IndicatorChartsView,
  PendingProposalsSection,
  ObservationFormDialog,
  ReviewDialog,
  DeleteConfirmDialog,
  ObservationsSkeleton,
} from '../components/observations';

// Important Vietnam indicators (default selection)
const IMPORTANT_VN_INDICATORS = [
  'CPI_YOY',
  'PMI',
  'CREDIT_YTD',
  'EXPORT_YOY',
  'USDVND',
  'GVB_10Y',
];

function Observations() {
  const { t } = useTranslation();
  
  // Chart states
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyTimeRange, setHistoryTimeRange] = useState('2Y'); // Changed from '1Y' to '2Y' to show policy rate changes
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedObservation] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    indicator_id: '',
    value: '',
    notes: '',
    source_url: '',
  });
  
  // Review states
  const [pendingProposals, setPendingProposals] = useState([]);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewProposal, setReviewProposal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Indicator details dialog
  const [openIndicatorDetails, setOpenIndicatorDetails] = useState(false);
  const [selectedIndicatorDetails, setSelectedIndicatorDetails] = useState(null);
  const [indicatorFullHistory, setIndicatorFullHistory] = useState([]);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);

  // Helper functions
  const getStartDateForRange = useCallback((range) => {
    const now = new Date();
    switch (range) {
      case '3M': return new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
      case '6M': return new Date(now.setMonth(now.getMonth() - 6)).toISOString().split('T')[0];
      case '1Y': return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      case '2Y': return new Date(now.setFullYear(now.getFullYear() - 2)).toISOString().split('T')[0];
      default: return null;
    }
  }, []);

  // API calls
  const fetchPendingProposals = useCallback(async () => {
    try {
      const response = await extractionReviewAPI.getPendingProposals();
      setPendingProposals(response.data);
    } catch (error) {
      console.error('Error fetching pending proposals:', error);
    }
  }, []);

  const fetchAllIndicatorHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const startDate = getStartDateForRange(historyTimeRange);
      const response = await observationsAPI.getHistory(startDate);
      
      const grouped = {};
      response.data.forEach(obs => {
        if (!grouped[obs.indicator_id]) {
          grouped[obs.indicator_id] = {
            indicator_id: obs.indicator_id,
            name_en: obs.indicator_name_en || obs.indicator_id,
            name_vi: obs.indicator_name_vi || obs.indicator_id,
            data: []
          };
        }
        grouped[obs.indicator_id].data.push({ date: obs.date, value: obs.value });
      });
      
      Object.values(grouped).forEach(ind => {
        ind.data.sort((a, b) => new Date(a.date) - new Date(b.date));
      });
      
      setHistoryData(grouped);
      
      // Set default selected indicators (only on initial load when no selection exists)
      if (Object.keys(grouped).length > 0 && selectedIndicators.length === 0) {
        const availableImportant = IMPORTANT_VN_INDICATORS.filter(
          ind => grouped[ind] && grouped[ind].data.length > 0
        );
        if (availableImportant.length > 0) {
          setSelectedIndicators(availableImportant);
        }
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyTimeRange, getStartDateForRange, selectedIndicators.length]);

  // Fetch chart data on mount and whenever the selected range changes
  useEffect(() => {
    fetchAllIndicatorHistory();
  }, [fetchAllIndicatorHistory]);

  // Initial data fetch
  useEffect(() => {
    fetchPendingProposals();
  }, [fetchPendingProposals]);

  // Event handlers
  const handleAddClick = () => {
    setEditForm({
      date: new Date().toISOString().split('T')[0],
      indicator_id: '',
      value: '',
      notes: '',
      source_url: '',
    });
    setOpenAddDialog(true);
  };


  const handleReviewClick = (proposal, action) => {
    setReviewProposal(proposal);
    setReviewAction(action);
    setReviewNotes('');
    setOpenReviewDialog(true);
  };

  // Save handlers
  const handleAddSave = async () => {
    try {
      await observationsAPI.create({
        date: editForm.date,
        indicator_id: editForm.indicator_id,
        value: parseFloat(editForm.value),
        notes: editForm.notes,
        source_url: editForm.source_url,
      });
      setOpenAddDialog(false);
      fetchAllIndicatorHistory();
    } catch (error) {
      console.error('Error adding observation:', error);
      alert('Error adding observation: ' + error.message);
    }
  };

  const handleEditSave = async () => {
    try {
      if (editForm.date !== selectedObservation.date || editForm.indicator_id !== selectedObservation.indicator_id) {
        await observationsAPI.delete(selectedObservation.date, selectedObservation.indicator_id);
        await observationsAPI.create({
          date: editForm.date,
          indicator_id: editForm.indicator_id,
          value: parseFloat(editForm.value),
          notes: editForm.notes,
          source_url: editForm.source_url,
        });
      } else {
        await observationsAPI.update(selectedObservation.date, selectedObservation.indicator_id, {
          value: parseFloat(editForm.value),
          notes: editForm.notes,
          source_url: editForm.source_url,
        });
      }
      setOpenEditDialog(false);
      fetchAllIndicatorHistory();
    } catch (error) {
      console.error('Error updating observation:', error);
      alert('Error updating observation: ' + error.message);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await observationsAPI.delete(selectedObservation.date, selectedObservation.indicator_id);
      setOpenDeleteDialog(false);
      fetchAllIndicatorHistory();
    } catch (error) {
      console.error('Error deleting observation:', error);
      alert('Error deleting observation: ' + error.message);
    }
  };

  const handleReviewSubmit = async () => {
    if (!reviewProposal) return;
    try {
      setReviewing(true);
      await extractionReviewAPI.reviewProposals(
        [reviewProposal.id],
        reviewAction,
        'admin',
        reviewNotes
      );
      setOpenReviewDialog(false);
      setReviewProposal(null);
      setReviewNotes('');
      fetchAllIndicatorHistory();
      fetchPendingProposals();
      alert(`Successfully ${reviewAction}ed proposal`);
    } catch (error) {
      console.error('Error reviewing proposal:', error);
      alert('Error: ' + error.message);
    } finally {
      setReviewing(false);
    }
  };

  // Handle indicator click to show full history
  const handleIndicatorClick = async (indicatorId) => {
    const indicator = historyData[indicatorId];
    if (!indicator) return;

    setSelectedIndicatorDetails(indicator);
    setOpenIndicatorDetails(true);
    setLoadingFullHistory(true);

    try {
      // Fetch all history (no date filter)
      const response = await observationsAPI.getHistory(null);
      const fullHistory = response.data
        .filter(obs => obs.indicator_id === indicatorId)
        .map(obs => ({
          date: obs.date,
          value: obs.value,
          notes: obs.notes,
          source_url: obs.source_url,
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort from newest to oldest
      
      setIndicatorFullHistory(fullHistory);
    } catch (error) {
      console.error('Error fetching full history:', error);
    } finally {
      setLoadingFullHistory(false);
    }
  };


  // Show skeleton during initial load
  if (loadingHistory && Object.keys(historyData).length === 0) {
    return <ObservationsSkeleton />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box 
        sx={{ 
          mb: 4,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                letterSpacing: -0.5,
              }}
            >
              {t('observations.title')}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              {t('observations.subtitle')}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ 
              whiteSpace: 'nowrap',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {t('observations.addObservation')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAllIndicatorHistory}
            sx={{ 
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>


      {/* Pending Proposals */}
      <PendingProposalsSection
        pendingProposals={pendingProposals}
        onReviewClick={handleReviewClick}
      />

      {/* Chart View */}
      <IndicatorChartsView
        historyData={historyData}
        loadingHistory={loadingHistory}
        selectedIndicators={selectedIndicators}
        setSelectedIndicators={setSelectedIndicators}
        historyTimeRange={historyTimeRange}
        setHistoryTimeRange={setHistoryTimeRange}
        onIndicatorClick={handleIndicatorClick}
      />


      {/* Dialogs */}
      <ObservationFormDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSave={handleAddSave}
        editForm={editForm}
        setEditForm={setEditForm}
        indicators={[]}
        mode="add"
      />

      <ObservationFormDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSave={handleEditSave}
        editForm={editForm}
        setEditForm={setEditForm}
        indicators={[]}
        mode="edit"
      />

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
      />

      <ReviewDialog
        open={openReviewDialog}
        onClose={() => setOpenReviewDialog(false)}
        onSubmit={handleReviewSubmit}
        proposal={reviewProposal}
        action={reviewAction}
        notes={reviewNotes}
        setNotes={setReviewNotes}
        loading={reviewing}
      />

      {/* Indicator Details Dialog */}
      <Dialog
        open={openIndicatorDetails}
        onClose={() => setOpenIndicatorDetails(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          }
        }}
      >
        {selectedIndicatorDetails && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace', mb: 0.5 }}>
                  {selectedIndicatorDetails.indicator_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedIndicatorDetails.name_vi || selectedIndicatorDetails.name_en}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setOpenIndicatorDetails(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha('#667eea', 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, overflowX: 'hidden' }}>
              {loadingFullHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Value</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {indicatorFullHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No historical data available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        indicatorFullHistory.map((item, idx) => (
                          <TableRow
                            key={idx}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                bgcolor: alpha('#667eea', 0.01),
                              },
                            }}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {new Date(item.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                              {typeof item.value === 'number' ? item.value.toFixed(2) : item.value}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', maxWidth: 300 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={item.notes || '-'}
                              >
                                {item.notes || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>
                              {item.source_url ? (
                                <Button
                                  size="small"
                                  href={item.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  View
                                </Button>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}

export default Observations;
